import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Switch } from 'src/@/components/ui/switch';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from 'src/@/components/ui/select';
import Iconify from 'src/components/Iconify';

// ----------------------------------------------------------------------
// Campos "conhecidos": mapeiam direto para o card de contato do LAIS (chat.info).
const KNOWN_FIELDS = [
    { key: 'nome', label: 'Nome', type: 'text' },
    { key: 'email', label: 'E-mail', type: 'email' },
    { key: 'telefone', label: 'Telefone', type: 'tel' },
    { key: 'cpf', label: 'CPF', type: 'text' },
    { key: 'cidade', label: 'Cidade', type: 'text' },
];
const KNOWN_KEYS = KNOWN_FIELDS.map((f) => f.key);

const TYPE_OPTIONS = [
    { value: 'text', label: 'Texto' },
    { value: 'email', label: 'E-mail' },
    { value: 'tel', label: 'Telefone' },
    { value: 'textarea', label: 'Texto longo' },
    { value: 'select', label: 'Seleção' },
];

const isCustom = (field) => field?.custom === true || !KNOWN_KEYS.includes(field?.key);

function nextCustomKey(fields) {
    let max = 0;
    (fields || []).forEach((f) => {
        const m = /^custom_(\d+)$/.exec(f?.key || '');
        if (m) max = Math.max(max, Number(m[1]));
    });
    return `custom_${max + 1}`;
}

// ----------------------------------------------------------------------

export default function ChannelWebchat({ canal, onChange }) {
    const { t } = useTranslation('settings');
    const preChat = canal?.config?.preChat || {};
    const enabled = preChat.enabled === true;
    const fields = Array.isArray(preChat.fields) ? preChat.fields : [];

    // Rótulos exibidos na UI — não alteram o `label` persistido em KNOWN_FIELDS/TYPE_OPTIONS
    // (esse continua fixo em PT, pois é gravado em preChat.fields e usado no widget do visitante).
    const knownFieldDisplayLabels = {
        nome: t('labels.name', { ns: 'common' }),
        email: t('channels.webchat.main.labelEmail', 'E-mail'),
        telefone: t('labels.phone', { ns: 'common' }),
        cpf: t('channels.webchat.main.labelCpf', 'CPF'),
        cidade: t('channels.webchat.main.labelCidade', 'Cidade'),
    };

    const typeOptionDisplayLabels = {
        text: t('channels.webchat.main.labelText', 'Texto'),
        email: t('channels.webchat.main.labelEmail', 'E-mail'),
        tel: t('labels.phone', { ns: 'common' }),
        textarea: t('channels.webchat.main.labelTextarea', 'Texto longo'),
        select: t('channels.webchat.main.labelSelect', 'Seleção'),
    };

    const commitFields = (newFields) => onChange({ target: { name: 'preChat.fields', value: newFields } });

    // --- Campos conhecidos (liga/desliga) ---
    const knownState = React.useMemo(() => {
        const byKey = new Map(fields.map((f) => [f.key, f]));
        return KNOWN_FIELDS.map((k) => ({ ...k, active: byKey.get(k.key), enabled: byKey.has(k.key) }));
    }, [fields]);

    const toggleKnown = (known, on) => {
        if (on) {
            commitFields([...fields, { key: known.key, label: known.label, type: known.type, required: false }]);
        } else {
            commitFields(fields.filter((f) => f.key !== known.key));
        }
    };

    const setKnownRequired = (key, required) => {
        commitFields(fields.map((f) => (f.key === key ? { ...f, required } : f)));
    };

    // --- Campos personalizados ---
    const customFields = fields.filter(isCustom);

    const addCustom = () => {
        commitFields([
            ...fields,
            { key: nextCustomKey(fields), label: '', type: 'text', required: false, custom: true },
        ]);
    };

    const updateCustom = (key, patch) => {
        commitFields(fields.map((f) => (f.key === key ? { ...f, ...patch } : f)));
    };

    const removeCustom = (key) => commitFields(fields.filter((f) => f.key !== key));

    return (
        <div className="flex flex-col gap-6 mt-2 animate-in fade-in duration-300">
            {/* Ativar */}
            <div className="flex items-center justify-between bg-white rounded-xl border border-gray-100 p-4">
                <div>
                    <p className="text-sm font-bold text-gray-900">{t('channels.webchat.main.title', 'Identificação do visitante')}</p>
                    <p className="text-xs text-gray-500">
                        {t('channels.webchat.main.subtitle', 'Exige que o visitante preencha os campos abaixo antes de iniciar a conversa.')}
                    </p>
                </div>
                <Switch
                    checked={enabled}
                    onCheckedChange={(value) => onChange({ target: { name: 'preChat.enabled', value } })}
                />
            </div>

            {enabled && (
                <>
                    {/* Textos do formulário */}
                    <div className="bg-white rounded-xl border border-gray-100 p-5 flex flex-col gap-4">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('channels.webchat.main.formTextsTitle', 'Textos do formulário')}</p>
                        <div className="flex flex-col gap-1.5">
                            <Label className="text-xs font-medium text-gray-600">{t('channels.webchat.main.titleLabel', 'Título')}</Label>
                            <Input
                                value={preChat.title || ''}
                                placeholder={t('channels.webchat.main.titlePlaceholder', 'Antes de começar')}
                                onChange={(e) => onChange({ target: { name: 'preChat.title', value: e.target.value } })}
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <Label className="text-xs font-medium text-gray-600">{t('channels.webchat.main.subtitleLabel', 'Subtítulo')}</Label>
                            <Input
                                value={preChat.subtitle || ''}
                                placeholder={t('channels.webchat.main.subtitlePlaceholder', 'Preencha seus dados para iniciar o atendimento')}
                                onChange={(e) => onChange({ target: { name: 'preChat.subtitle', value: e.target.value } })}
                            />
                        </div>
                    </div>

                    {/* Campos padrão */}
                    <div className="bg-white rounded-xl border border-gray-100 p-5 flex flex-col gap-3">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('channels.webchat.main.standardFieldsTitle', 'Campos padrão')}</p>
                        <p className="text-xs text-gray-400 -mt-1">
                            {t('channels.webchat.main.standardFieldsHint', 'Estes campos preenchem automaticamente o cadastro de contato do atendimento.')}
                        </p>
                        {knownState.map((k) => (
                            <div key={k.key} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                                <div className="flex items-center gap-3">
                                    <Switch checked={k.enabled} onCheckedChange={(on) => toggleKnown(k, on)} />
                                    <span className="text-sm text-gray-800">{knownFieldDisplayLabels[k.key] || k.label}</span>
                                </div>
                                {k.enabled && (
                                    <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
                                        {t('labels.required', { ns: 'common' })}
                                        <Switch
                                            checked={k.active?.required === true}
                                            onCheckedChange={(required) => setKnownRequired(k.key, required)}
                                        />
                                    </label>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Campos personalizados */}
                    <div className="bg-white rounded-xl border border-gray-100 p-5 flex flex-col gap-3">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('channels.webchat.main.customFieldsTitle', 'Campos personalizados')}</p>
                                <p className="text-xs text-gray-400 mt-0.5">
                                    {t('channels.webchat.main.customFieldsHint', 'Ficam registrados nas observações do atendimento.')}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={addCustom}
                                className="flex items-center gap-1.5 px-3 py-2 bg-[#7C3AED] hover:bg-[#6d28d9] text-white text-xs font-medium rounded-xl transition-all shadow-sm shadow-[#7C3AED]/20 active:scale-95"
                            >
                                <Iconify icon="eva:plus-outline" width={16} />
                                {t('channels.webchat.main.addFieldButton', 'Adicionar campo')}
                            </button>
                        </div>

                        {customFields.length === 0 && (
                            <p className="text-xs text-gray-400 py-2">{t('channels.webchat.main.emptyCustomFields', 'Nenhum campo personalizado.')}</p>
                        )}

                        {customFields.map((f) => (
                            <div key={f.key} className="flex flex-col md:flex-row md:items-end gap-3 p-3 rounded-xl bg-gray-50/60 border border-gray-100">
                                <div className="flex flex-col gap-1.5 flex-1">
                                    <Label className="text-xs font-medium text-gray-600">{t('channels.webchat.main.labelInputLabel', 'Rótulo')}</Label>
                                    <Input
                                        value={f.label || ''}
                                        placeholder={t('channels.webchat.main.labelPlaceholder', 'Ex.: Assunto')}
                                        onChange={(e) => updateCustom(f.key, { label: e.target.value })}
                                    />
                                </div>
                                <div className="flex flex-col gap-1.5 w-full md:w-40">
                                    <Label className="text-xs font-medium text-gray-600">{t('channels.webchat.main.typeInputLabel', 'Tipo')}</Label>
                                    <Select value={f.type || 'text'} onValueChange={(value) => updateCustom(f.key, { type: value })}>
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {TYPE_OPTIONS.map((opt) => (
                                                <SelectItem key={opt.value} value={opt.value}>
                                                    {typeOptionDisplayLabels[opt.value] || opt.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                {f.type === 'select' && (
                                    <div className="flex flex-col gap-1.5 flex-1">
                                        <Label className="text-xs font-medium text-gray-600">{t('channels.webchat.main.optionsInputLabel', 'Opções (separadas por vírgula)')}</Label>
                                        <Input
                                            value={(f.options || []).join(', ')}
                                            placeholder={t('channels.webchat.main.optionsPlaceholder', 'Vendas, Suporte, Financeiro')}
                                            onChange={(e) =>
                                                updateCustom(f.key, {
                                                    options: e.target.value
                                                        .split(',')
                                                        .map((o) => o.trim())
                                                        .filter(Boolean),
                                                })
                                            }
                                        />
                                    </div>
                                )}
                                <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer whitespace-nowrap pb-2">
                                    {t('labels.required', { ns: 'common' })}
                                    <Switch
                                        checked={f.required === true}
                                        onCheckedChange={(required) => updateCustom(f.key, { required })}
                                    />
                                </label>
                                <button
                                    type="button"
                                    onClick={() => removeCustom(f.key)}
                                    className="w-9 h-9 flex items-center justify-center rounded-xl text-red-500 hover:bg-red-50 transition-colors mb-1"
                                    aria-label={t('channels.webchat.main.removeFieldAriaLabel', 'Remover campo')}
                                >
                                    <Iconify icon="eva:trash-2-outline" width={18} />
                                </button>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
